import { type FormEvent, useState } from "react";
import { Navigate, useLocation } from "react-router-dom";
import { useEmailSignIn } from "../lib/coinbase/useEmailSignIn";
import { useAuthPrincipal } from "../hooks/useAuthPrincipal";
import { usePublicConfig } from "../hooks/usePublicConfig";
import { usePageMeta } from "../lib/seo";
import { TurnstileWidget } from "../components/TurnstileWidget";

export function SignInPage() {
  usePageMeta({
    title: "Sign in",
    description: "Sign in to MarketOnBase with your email — no seed phrase or extension required.",
    path: "/sign-in",
    index: false,
  });

  const { isSignedIn } = useAuthPrincipal();
  const location = useLocation();
  const { step, email, isSubmitting, error, requestOtp, verifyOtp, reset } = useEmailSignIn();
  const [emailInput, setEmailInput] = useState("");
  const [otpInput, setOtpInput] = useState("");
  const { data: publicConfig } = usePublicConfig();

  if (isSignedIn) {
    const from = (location.state as { from?: string } | null)?.from ?? "/";
    return <Navigate to={from} replace />;
  }

  return (
    <div className="stack" style={{ maxWidth: 420, margin: "0 auto" }}>
      {/* Mounted unconditionally (not gated on `step`) so it has the whole sign-in flow's
          duration to solve/auto-refresh in the background — see TurnstileWidget's docblock.
          Renders nothing when Turnstile isn't configured server-side (local dev, etc). */}
      {publicConfig?.turnstileSiteKey && (
        <TurnstileWidget siteKey={publicConfig.turnstileSiteKey} />
      )}

      <div>
        <h1>Sign in</h1>
        <p className="muted">
          Sign in with your email to get a Coinbase-managed Smart Account on Base — no browser
          extension, seed phrase, or private key required. MarketOnBase never has access to your
          keys.
        </p>
      </div>

      <div className="card">
        {step === "enter-email" && (
          <form
            className="stack"
            onSubmit={(e: FormEvent) => {
              e.preventDefault();
              void requestOtp(emailInput.trim());
            }}
          >
            <div className="field">
              <label htmlFor="email">Email address</label>
              <input
                id="email"
                type="email"
                required
                autoComplete="email"
                value={emailInput}
                onChange={(e) => setEmailInput(e.target.value)}
              />
            </div>
            {error && (
              <p className="field-error-text" role="alert">
                {error}
              </p>
            )}
            <button type="submit" className="btn btn-primary btn-block" disabled={isSubmitting}>
              {isSubmitting ? "Sending code…" : "Send sign-in code"}
            </button>
          </form>
        )}

        {step === "enter-otp" && (
          <form
            className="stack"
            onSubmit={(e: FormEvent) => {
              e.preventDefault();
              void verifyOtp(otpInput.trim());
            }}
          >
            <p className="row success-pop" style={{ gap: "0.5rem" }}>
              <span className="success-check" aria-hidden="true">
                ✓
              </span>
              <span>
                Code sent to <strong>{email}</strong>.
              </span>
            </p>
            <div className="field">
              <label htmlFor="otp">Verification code</label>
              <input
                id="otp"
                type="text"
                inputMode="numeric"
                autoComplete="one-time-code"
                required
                value={otpInput}
                onChange={(e) => setOtpInput(e.target.value)}
              />
            </div>
            {error && (
              <p className="field-error-text" role="alert">
                {error}
              </p>
            )}
            <button type="submit" className="btn btn-primary btn-block" disabled={isSubmitting}>
              {isSubmitting ? "Verifying…" : "Verify and sign in"}
            </button>
            <button type="button" className="btn-link" onClick={reset}>
              Use a different email
            </button>
          </form>
        )}

        {step === "signed-in" && <p>You&apos;re signed in. Redirecting…</p>}
      </div>
    </div>
  );
}
